import { forwardRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { darken, lighten, rgba } from "polished";

const BaseButton = styled.button(({
    growHorizontal,
    growVertical,
    noMargin,
    noPadding,
    textAlign,
    theme: { fontSizeLarge, text }
}) => `
    display: inline-block;
    font-family: ${text.regular};
    font-size: ${fontSizeLarge};
    white-space: nowrap;
    vertical-align: middle;
    touch-action: manipulation;
    cursor: pointer;
    background-color: transparent;
    background-image: "none";
    border: 1px solid transparent;
    padding: ${noPadding ? 0 : "6px 12px"};
    margin: ${noMargin ? 0 : "6px"};
    text-align: ${textAlign};
    height: ${growVertical ? "100%" : "auto"};
    width: ${growHorizontal ? "100%" : "auto"};
    line-height: 1.42857;
    border-radius: 4px;
    user-select: none;
    text-decoration: none;

    svg {
        margin: 0 10px 0 0;
    }
`);

BaseButton.propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    growVertical: PropTypes.bool,
    growHorizontal: PropTypes.bool,
    name: PropTypes.string,
    noMargin: PropTypes.bool,
    noPadding: PropTypes.bool,
    onClick: PropTypes.func,
    textAlign: PropTypes.oneOf(["start", "end", "left", "center", "right", "justify"]),
    type: PropTypes.oneOf(["button", "reset", "submit"]),
    value: PropTypes.string
};

BaseButton.defaultProps = {
    className: "",
    growVertical: false,
    growHorizontal: false,
    noMargin: false,
    noPadding: false,
    textAlign: "center",
    type: "button",
    disabled: false,
    onClick: () => {}
};

const BaseAnchor = styled.a.attrs(({ href, rel, target }) => {
    if (target === "_blank") {
        // When creating a JSX element that has an a tag, it is often desired to have the link open in a new tab using the target='_blank' attribute.
        // Using this attribute unaccompanied by rel='noreferrer', however, is a severe security vulnerability
        // https://web.dev/external-anchors-use-rel-noopener/
        return {
            href,
            target,
            rel: rel || "noopener noreferrer"
        };
    }
    
    return { href, rel, target };
})(({ noMargin, noPadding, theme: { colors } }) => ({
    margin: noMargin ? 0 : "0 6px",
    padding: noPadding ? 0 : "6px",
    lineHeight: 1.428_57,
    cursor: "pointer",

    "&:link, &:visited": {
        color: colors["paz-black"],
        textDecoration: "underline"
    },

    "&:hover,  &:active": {
        color: colors["blue-wcag-327"]
    }
}));

BaseAnchor.propTypes = {
    className: PropTypes.string,
    href: PropTypes.string.isRequired,
    noMargin: PropTypes.bool,
    noPadding: PropTypes.bool,
    onClick: PropTypes.func
};

BaseAnchor.defaultProps = {
    className: "",
    href: "",
    noMargin: false,
    noPadding: false,
    onClick: () => {}
};

export const Button = forwardRef(({
    children,
    ...rest
}, ref) => {
    if (rest?.href) {
        return (
            <BaseAnchor ref={ref} {...rest} >
                {children}
            </BaseAnchor>
        );
    }
    return (
        <BaseButton ref={ref} {...rest} >
            {children}
        </BaseButton>
    );
});

export const PrimaryButton = styled(Button)(({ theme: { colors } }) => ({
    color: colors["canvas-white"],
    backgroundColor: colors["blue-wcag-327"],
    borderColor: colors["blue-wcag-327"],

    "&:hover, &:active": {
        color: colors["canvas-white"],
        borderColor: colors["blue-wcag-327"],
        backgroundColor: darken(0.1, colors["blue-wcag-327"])
    },

    "&:focus": {
        color: colors["canvas-white"],
        borderColor: darken(0.2, colors["canvas-white"]),
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`
    },

    "&:disabled, &.disabled": {
        color: lighten(0.6, colors["canvas-white"]),
        backgroundColor: lighten(0.4, colors["blue-wcag-327"]),
        borderColor: lighten(0.4, colors["blue-wcag-327"])
    }
}));

export const SecondaryButton = styled(Button)(({ theme: { colors } }) => ({
    backgroundColor: colors["canvas-white"],
    borderColor: colors["blue-wcag-327"],
    color: colors["blue-wcag-327"],

    "&:hover, &:active": {
        background: colors["blue-tint"],
        borderColor: colors["blue-wcag-327"],
        color: colors["blue-wcag-327"]
    },

    "&:disabled, &.disabled": {
        color: rgba(colors["blue-wcag-327"], 0.6),
        backgroundColor: rgba(colors["canvas-white"], 0.4),
        borderColor: rgba(colors["blue-wcag-327"], 0.6)
    },

    "&:focus": {
        backgroundColor: colors["blue-tint"],
        border: `1px solid ${colors["blue-wcag-327"]}`,
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`,
        color: colors["blue-wcag-327"]
    }
}));

export const SecondaryButtonAlt = styled(Button)(({ theme: { colors } }) => ({
    backgroundColor: colors["blue-tint"],
    
    "&:hover, &:active": {
        color: darken(0.2, colors["blue-wcag-327"]),
        backgroundColor: colors["blue-mild"]
    },

    "&:disabled, &.disabled": {
        color: rgba(colors["blue-wcag-327"], 0.6),
        backgroundColor: rgba(colors["blue-tint"], 0.4),
        borderColor: rgba(colors["blue-wcag-327"], 0.4)
    },

    "&:focus": {
        color: darken(0.2, colors["blue-wcag-327"]),
        backgroundColor: colors["blue-mild"],
        borderColor: colors["blue-wcag-327"],
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`
    }
}));

export const CancelButton = styled(Button)(({ theme: { colors } }) => ({
    color: colors["grey-wcag-6B6"],
    backgroundColor: colors["canvas-white"],
    borderColor: colors["paz-black-l-30"],

    "&:active, &:hover": {
        backgroundColor: colors["canvas-dark"]
    },

    "&:disabled, &.disabled": {
        color: rgba(colors["grey-wcag-6B6"], 0.6),
        backgroundColor: rgba(colors["canvas-white"], 0.4),
        borderColor: rgba(colors["grey-wcag-6B6"], 0.6)
    },

    "&:focus": {
        backgroundColor: colors["canvas-dark"],
        border: `1px solid ${colors["canvas-white"]}`,
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`
    }
}));

export const TertiaryButton = styled(Button)(({ theme: { colors } }) => ({
    color: colors["blue-wcag-327"],
    backgroundColor: colors["canvas-white"],

    "&:hover, &:active": {
        color: darken(0.2, colors["blue-wcag-327"]),
        backgroundColor: colors["paz-black-l-97"],
        boxShadow: "none"
    },

    "&:focus": {
        color: darken(0.6, colors["blue-wcag-327"]),
        backgroundColor: colors["paz-black-l-97"],
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`
    },

    "&:disabled, &.disabled": {
        color: rgba(colors["blue-wcag-327"], 0.6),
        backgroundColor: rgba(colors["canvas-white"], 0.4)
    }
}));

export const TertiaryButtonAlt = styled(TertiaryButton)(({ theme: { colors } }) => ({
    "&:hover, &:active": {
        color: colors["canvas-white"],
        backgroundColor: colors["blue-wcag-327"]
    },

    "&:focus": {
        color: colors["blue-wcag-327"],
        background: colors["canvas-white"],
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`
    },

    "&:disabled, &.disabled": {
        color: rgba(colors["blue-wcag-327"], 0.6),
        backgroundColor: rgba(colors["canvas-white"], 0.4)
    }
}));

export const PlaceholderButton = styled(TertiaryButton)(({ theme: { colors } }) => ({
    color: colors["grey-wcag-6B6"],
    backgroundColor: colors["canvas-white"],
    border: `1px dashed ${colors["gray-light"]}`,

    "&:hover, &:active": {
        color: colors["grey-wcag-6B6"],
        backgroundColor: colors["canvas-white"],
        border: `1px dashed ${colors["grey-wcag-6B6"]}`
    },

    "&:focus": {
        color: colors["grey-wcag-6B6"],
        backgroundColor: colors["canvas-white"],
        border: "1px dashed transparent",
        boxShadow: `0 0 0 1px solid ${colors["grey-wcag-6B6"]}`
    },

    "&:disabled, &.disabled": {
        color: rgba(colors["grey-wcag-6B6"], 0.6),
        backgroundColor: rgba(colors["canvas-white"], 0.4)
    }
}));

export const Anchor = styled(Button)(({ theme: { colors } }) => ({
    backgroundColor: "transparent",
    textDecoration: "underline",
    color: colors["paz-black"],
    
    "&:hover,  &:active": {
        color: colors["blue-wcag-327"]
    },

    "&:focus": {
        boxShadow: `0 0 0 1px ${colors["blue-wcag-327"]}`
    }
}));

export const TableAnchor = styled(Button)(({ theme: { colors } }) => ({
    "&:link, &:visited": {
        color: colors["paz-black"],
        textDecoration: "none"
    },
    
    "&:hover,  &:active": {
        textDecoration: "underline",
        backgroundColor: colors["canvas-mid"]
    },

    "&:focus": {
        color: colors["blue-wcag-327"],
        textDecoration: "underline"
    }
}));

export const ColorAnchor = styled(BaseAnchor)(({ color: propsColor, theme: { text, colors } }) => ({
    "&:link, &:visited": {
        color: colors[propsColor],
        textDecoration: "underline"
    },
    
    "&:hover,  &:active": {
        color: colors[propsColor],
        textDecoration: "underline",
        fontFamily: text.medium
    },
    
    "&:focus": {
        color: colors[propsColor],
        textDecoration: "underline",
        fontFamily: text.medium
    }
}));

ColorAnchor.propTypes = {
    href: PropTypes.string.isRequired,
    color: PropTypes.string
};

ColorAnchor.defaultProps = {
    color: "grey-wcag-6B6"
};
